import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useIdentity from "@/features/ui/hooks/use-identity";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ToBeUpdatedOrderSheet } from "@/features/order-status/modals/to-be-updated-order-sheet-list";
import useSubmitOrderSheetSet, {
  SubmitOrderSheetSetResponse,
} from "@/features/order-sheet-sets/hooks/use-submit-order-sheet-set";
import {
  Box,
  Flex,
  Grid,
  GridItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { toTitle } from "@/utils/case";
import * as amplitude from "@amplitude/analytics-browser";
import { useRecoilValue } from "recoil";
import { inflatedTabsFamily } from "@/features/line-sheet-sets/helpers/sheet-state";
import useSubmitOrderSheetSetV2, {
  SubmitOrderSheetSetV2Response,
} from "@/features/order-sheet-sets/hooks/use-submit-order-sheet-set-v2";
import { OrderSheetProduct } from "@/features/order-sheets/order-sheet.type";
import { AppToolTipProps } from "@/features/ui/app-tooltip";
import { CommissionType } from "@/features/line-sheet-sets/line-sheet-set.types";
import MoqNoticeBanner, {
  checkOrderAmount,
} from "@/features/order-sheet-sets/moq-notice-banner";
import OrderSubmitUnderMinimum from "@/features/order-sheet-sets/order-submit-under-minimum";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import useAppDialog from "@/features/ui/hooks/use-app-dialog";
import useCreateOrderSheetRevisionV2, {
  CreateOrderSheetRevisionV2Response,
} from "@/features/line-sheet-sets/hooks/use-create-order-sheet-revision-v2";

export interface OrderOrderSheetSetModalV2Props {
  orderSheetSetId: number;
  onSubmit: (response: SubmitOrderSheetSetV2Response) => void;
  isOpen: boolean;
  onClose: () => void;
  isOrderSheetDirty?: boolean;
  orderSheetTabs?: {
    index: number;
    products: OrderSheetProduct[];
  }[];
  // 아래 셋은 페이지가 이미 들고 있는 값을 그대로 받는다.
  // 모달에서 다시 조회하면 오더시트세트/라인시트세트/리비전을 중복으로 부르게 된다.
  commission?: number | null;
  commissionType?: CommissionType | null;
  // 라인시트세트의 MOQ(화면에서는 '권장주문금액'). 총 주문금액이 최소주문금액(제출 하한, 유로 5,000)보다 작으면
  // 제출 화면 대신 '오더 제출 안내'를 띄우고, 그 위~MOQ 미만이면 제출 화면에 권장주문금액 안내를 붙인다.
  moq?: number | null;
  // 소량 주문 가능 세트(패스픽 오픈 중인 스탁오더)인지. true 면 최소주문금액으로 제출을 막지 않고(서버도 같다)
  // 권장주문금액 안내도 붙이지 않는다.
  canOrderByUnit?: boolean;
  // 리비전이 이미 있는 재제출인지. 모르면(로딩 등) 예전 동작대로 true 취급.
  hasRevision?: boolean;
  // 작성 중인 주문서(저장 버튼이 있는 상태)인지. true 면 최소주문금액 미달 안내에서 패스픽으로 떠날 때
  // 변경분을 저장하고 간다. false(이미 제출한 오더를 수정 중)면 저장이 없어 '변경사항 버리기' 확인을 받는다.
  isDraftSavable?: boolean;
  // 위 저장에 성공했을 때 — 페이지가 상태·더티 표시를 정리한다(저장 버튼의 onAfterSave 와 같은 처리).
  onAfterDraftSave?: (response: CreateOrderSheetRevisionV2Response) => void;
  // 시트 아톰 키를 만들 때 쓴다. URL(query.revisionNumber)에서 직접 읽으면
  // URL에 revision_number가 없는 진입에서 키가 어긋나 모달이 비어버린다.
  // 다른 자식들과 동일하게 페이지가 들고 있는 값을 그대로 받는다.
  revisionNumber?: number;
}

export default function OrderOrderSheetSetModalV2({
  orderSheetSetId,
  onSubmit,
  isOpen,
  onClose,
  orderSheetTabs,
  isOrderSheetDirty,
  commission,
  commissionType,
  moq,
  canOrderByUnit = false,
  hasRevision = true,
  isDraftSavable = false,
  onAfterDraftSave,
  revisionNumber,
}: OrderOrderSheetSetModalV2Props) {
  const identity = useIdentity();
  const { t, tCurrencyString, tTitle } = useI18nHelper();
  const { navigate } = useRouterHelper();
  const { openDialog } = useAppDialog();

  const company = useMemo(() => {
    return identity?.company;
  }, [identity]);

  const { error: showError, info: showInfo } = useAppToasts({ id: "GLOBAL" });
  const { fire: fireSaveDraft } = useCreateOrderSheetRevisionV2();
  const [isLeavingToPathpick, setIsLeavingToPathpick] = useState(false);
  // '협의된 추가 오더로 제출'을 고른 때의 주문 상태(세트·리비전·금액·하한). 주문이 바뀌면 다시 묻도록
  // 이 값과 지금 상태가 같을 때만 추가 오더 모드로 본다.
  const [additionalOrderKey, setAdditionalOrderKey] = useState<string | null>(
    null
  );

  const inflatedTabs = useRecoilValue(
    inflatedTabsFamily({
      orderSheetSetId: orderSheetSetId,
      orderSheetId: -1,
      revisionNumber: revisionNumber || -1,
    })
  );

  // 수수료 계산 단위가 ORDER인 경우, 상품 단가에는 수수료가 빠져 있으므로
  // 브랜드별/총 주문금액에만 수수료를 반영한다.
  const commissionFactor = useMemo(() => {
    return commissionType === "ORDER" && commission ? 1 + commission / 100 : 1;
  }, [commission, commissionType]);

  //수량으로 확인이 필요해졌다, 통계과 함께 연산을 하도록 변경,
  const payload = useMemo(() => {
    if (inflatedTabs) {
      const toBeOrderedOrderSheets: ToBeUpdatedOrderSheet[] = [];
      const toBeCancelledOrderSheets: ToBeUpdatedOrderSheet[] = [];
      const toBeUpdatedOrderSheetStatistic: {
        name: string;
        totalQuantity: number | "-";
        totalAmount: number | "-";
      }[] = [];

      //브랜드별 총 주문 금액, 수량을 구한다.
      const AllBrands = inflatedTabs.tabs
        .map((tab, index) => {
          return tab.products
            .filter((product) =>
              product.orderQuantityWithOptionList.some(
                (option) => option.quantity > 0
              )
            )
            .map((product) => product.brandName);
        })
        .flat();
      const uniqueAllBrands = Array.from(new Set(AllBrands));

      uniqueAllBrands.forEach((brand) => {
        if (!brand) return;
        const productsOfBrand = inflatedTabs.tabs
          .map((tab) => {
            return tab.products.filter(
              (product) =>
                product.brandName === brand &&
                product.orderQuantityWithOptionList.some(
                  (option) => option.quantity > 0
                )
            );
          })
          .flat();

        let brandTotalQuantity = 0;
        let brandTotalAmount = 0;

        productsOfBrand.forEach((product) => {
          let priceAmount = product.supplyPrice?.amount!;
          product.orderQuantityWithOptionList.forEach((option) => {
            if (option.quantity > 0) {
              brandTotalQuantity += option.quantity;
              brandTotalAmount += option.quantity * priceAmount;
            }
          });
        });

        toBeUpdatedOrderSheetStatistic.push({
          name: brand,
          totalQuantity: brandTotalQuantity,
          // 수수료 계산 단위가 ORDER면 합계에 수수료 반영(상품 단가에는 미반영)
          totalAmount: brandTotalAmount * commissionFactor,
        });
      });

      if (toBeUpdatedOrderSheetStatistic.length > 0) {
        //모두 fallback인경우

        const isAllFallback = toBeUpdatedOrderSheetStatistic.every(
          (statistic) => statistic.totalAmount === "-"
        );

        if (isAllFallback) {
          toBeUpdatedOrderSheetStatistic.unshift({
            name: "Total",
            totalQuantity: "-",
            totalAmount: "-",
          });
        } else {
          const total = toBeUpdatedOrderSheetStatistic.reduce(
            (
              acc,
              cur
            ): { name: string; totalQuantity: number; totalAmount: number } => {
              function func(value: number | "-"): number {
                if (value === "-") {
                  return 0;
                } else {
                  return value;
                }
              }

              return {
                ...acc,
                totalQuantity:
                  func(acc.totalQuantity) + func(cur.totalQuantity),
                totalAmount: func(acc.totalAmount) + func(cur.totalAmount),
              };
            },
            {
              name: "Total",
              totalQuantity: 0,
              totalAmount: 0,
            }
          );
          toBeUpdatedOrderSheetStatistic.unshift(total);
        }
      }

      // 금액은 상품 공급가 통화 그대로다(원화 라인시트면 원화) — 표시할 때 이 통화를 쓴다
      const currency =
        inflatedTabs.tabs
          .flatMap((tab) => tab.products)
          .find((product) => product.supplyPrice?.currency)?.supplyPrice
          ?.currency ?? "EUR";

      return {
        toBeOrderedOrderSheets,
        toBeCancelledOrderSheets,
        toBeUpdatedOrderSheetStatistic,
        currency,
      };
    }

    return {
      toBeOrderedOrderSheets: [],
      toBeCancelledOrderSheets: [],
      toBeUpdatedOrderSheetStatistic: [],
      currency: "EUR",
    };
  }, [inflatedTabs, commissionFactor]);

  const {
    isLoading: isSubmitOrderSheetSetLoading,
    fire: fireSubmitOrderSheetSet,
    data: fireSubmitOrderSheetSetData,
    error: fireSubmitOrderSheetSetError,
    clear: clearSubmitOrderSheetSet,
  } = useSubmitOrderSheetSetV2();

  useEffect(() => {
    showError(fireSubmitOrderSheetSetError);
  }, [showError, fireSubmitOrderSheetSetError]);

  useEffect(() => {
    if (fireSubmitOrderSheetSetData) {
      onSubmit(fireSubmitOrderSheetSetData);
      clearSubmitOrderSheetSet();
      onClose();
    }
  }, [
    clearSubmitOrderSheetSet,
    fireSubmitOrderSheetSetData,
    onClose,
    onSubmit,
  ]);

  // 합계 행 표시와 같은 값(수수료 반영)으로 최소·권장주문금액과 비교한다.
  const totalAmount = payload.toBeUpdatedOrderSheetStatistic[0]?.totalAmount;
  const amountCheck = checkOrderAmount(
    totalAmount,
    moq,
    payload.currency,
    canOrderByUnit
  );
  const currentOrderKey = [
    orderSheetSetId,
    revisionNumber,
    totalAmount,
    amountCheck.minimumAmount,
    payload.currency,
  ].join(":");
  // 최소주문금액 미달이어도 '협의된 추가 오더'로 제출하기로 했으면 제출 화면을 띄운다(서버도 받는다).
  const isAdditionalOrder = additionalOrderKey === currentOrderKey;
  const isSubmitBlocked = amountCheck.stage === "BLOCKED" && !isAdditionalOrder;
  // 추가 오더 제출은 담은 금액이 있을 때만 — 빈 주문을 추가 오더로 보낼 일은 없다.
  const canSubmitAsAdditionalOrder =
    typeof totalAmount === "number" &&
    Number.isFinite(totalAmount) &&
    totalAmount > 0;

  // 모달을 닫거나 다른 오더로 바뀌면 추가 오더 선택을 잊는다.
  useEffect(() => {
    setAdditionalOrderKey(null);
  }, [isOpen, orderSheetSetId, revisionNumber]);

  const handleTriggerClick = useCallback(() => {
    if (isSubmitOrderSheetSetLoading || isSubmitBlocked) return;
    if (company?.type === "BUYER") {
      fireSubmitOrderSheetSet(
        {
          buyerId: company.id,
          orderSheetSetId,
        },
        {
          orderSheetTabs:
            hasRevision && !isOrderSheetDirty ? undefined : orderSheetTabs,
          ...(isAdditionalOrder ? { isAdditionalOrder: true } : {}),
        }
      );
      amplitude.track("Order Submitted");
    }
  }, [
    isSubmitOrderSheetSetLoading,
    isSubmitBlocked,
    company?.type,
    company?.id,
    fireSubmitOrderSheetSet,
    orderSheetSetId,
    orderSheetTabs,
    hasRevision,
    isOrderSheetDirty,
    isAdditionalOrder,
  ]);

  const {
    toBeOrderedOrderSheets,
    toBeCancelledOrderSheets,
    toBeUpdatedOrderSheetStatistic,
    currency,
  } = payload;

  const isOrderable = true;

  // 패스픽으로 떠나기 — 저장하지 않은 수량이 있으면 잃지 않게 처리하고 간다.
  //  - 작성 중인 주문서: 저장 버튼과 같은 요청으로 저장한 뒤 이동한다(실패하면 머문다).
  //  - 이미 제출한 오더를 수정 중: 저장이라는 게 없어 '변경사항 버리기' 확인을 받는다(뒤로 가기와 같은 확인창).
  const handlePathpickClick = async () => {
    if (isLeavingToPathpick) {
      return;
    }
    if (isOrderSheetDirty) {
      if (isDraftSavable && company?.type === "BUYER" && orderSheetTabs) {
        setIsLeavingToPathpick(true);
        try {
          const response = await fireSaveDraft(
            { by: company.type, companyId: company.id, orderSheetSetId },
            {
              // 저장 버튼과 같은 모양 — 바이어는 확정 수량을 보내지 않는다
              orderSheetTabs: orderSheetTabs.map((tab) => ({
                ...tab,
                products: tab.products.map((product) => ({
                  ...product,
                  confirmedQuantityWithOptionList: null,
                })),
              })),
            },
            { rethrow: true }
          );
          onAfterDraftSave?.(response);
          showInfo(t("moq_notice.under_min.draft_saved"));
        } catch (e) {
          showError(e);
          setIsLeavingToPathpick(false);
          return;
        }
      } else {
        const isConfirmed = await openDialog({
          title: tTitle("common:discard_changes"),
          message: t("common:discard_changes.message"),
          confirmName: tTitle("continue"),
        });
        if (!isConfirmed) {
          return;
        }
      }
    }
    onClose();
    navigate("/pathpick/search-products");
  };

  // 최소주문금액 미달 — 제출 화면 대신 안내만 띄운다(부족 금액 + 패스픽 유도). 제출 버튼이 없다.
  if (isSubmitBlocked && amountCheck.minimumAmount !== null) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size={"xl"}
        isCentered={true}
        scrollBehavior={"inside"}
        closeOnOverlayClick={!isLeavingToPathpick}
        closeOnEsc={!isLeavingToPathpick}
      >
        <ModalOverlay />
        <ModalContent
          maxWidth={"600px"}
          marginX={"16px"}
          maxHeight={"calc(100dvh - 32px)"}
        >
          <ModalHeader
            color={"#444440"}
            fontSize={"14px"}
            background={"#F7F9FA"}
            borderBottom={"0.5px solid #8F8F8C"}
            borderRadius={"8px 8px 0 0"}
          >
            {t("moq_notice.under_min.title")}
          </ModalHeader>
          <ModalCloseButton
            aria-label={tTitle("close")}
            isDisabled={isLeavingToPathpick}
          />
          <OrderSubmitUnderMinimum
            currentAmount={totalAmount === "-" ? undefined : totalAmount ?? 0}
            minimumAmount={amountCheck.minimumAmount}
            shortfall={amountCheck.shortfall}
            currency={currency}
            onEdit={onClose}
            onPathpick={handlePathpickClick}
            isPathpickLoading={isLeavingToPathpick}
            onAdditionalOrder={
              canSubmitAsAdditionalOrder
                ? () => setAdditionalOrderKey(currentOrderKey)
                : undefined
            }
          />
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={"3xl"}
      closeOnOverlayClick={!isSubmitOrderSheetSetLoading}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader
          color={"#444440"}
          fontSize={"14px"}
          background={"#F7F9FA"}
          borderBottom={"0.5px solid #8F8F8C"}
          borderRadius={"8px 8px 0 0"}
        >
          {isAdditionalOrder
            ? t("moq_notice.additional_order.title")
            : tTitle("order")}
        </ModalHeader>
        <ModalBody>
          {isOrderable && (
            <Flex flexDirection={"column"} gap={"20px"}>
              {isAdditionalOrder && (
                <Box
                  background={"#F7F9FA"}
                  color={"#444440"}
                  borderRadius={"4px"}
                  padding={"8px 12px"}
                  fontSize={"13px"}
                  lineHeight={"20px"}
                >
                  {t("moq_notice.additional_order.notice")}
                </Box>
              )}
              <Box fontSize={"12px"}>{t("order_form.messages.caution")}</Box>
              {toBeUpdatedOrderSheetStatistic && (
                <Grid
                  gridTemplateColumns={"1fr 0.4fr 0.4fr"}
                  gridAutoRows={"24px"}
                  alignItems={"end"}
                  justifyItems={"stretch"}
                  fontSize={"12px"}
                >
                  <GridItem
                    borderBottom={"0.5px solid var(--gray, #D4D4D1)"}
                    justifySelf={"stretch"}
                  >
                    {toTitle(t("common:brand"))}
                  </GridItem>
                  <GridItem
                    borderBottom={"0.5px solid var(--gray, #D4D4D1)"}
                    justifySelf={"stretch"}
                    textAlign={"end"}
                  >
                    {toTitle(t("common:amount"))}
                  </GridItem>
                  <GridItem
                    borderBottom={"0.5px solid var(--gray, #D4D4D1)"}
                    justifySelf={"stretch"}
                    textAlign={"end"}
                  >
                    {toTitle(t("common:quantity.abbr"))}
                  </GridItem>
                  {toBeUpdatedOrderSheetStatistic
                    .slice(1)
                    .map((statistic, index) => {
                      const { name, totalQuantity, totalAmount } = statistic;
                      const borderBottom = "";
                      return (
                        <React.Fragment
                          key={
                            "SubmitOrderSheetSetModalStatistic_" +
                            orderSheetSetId +
                            "_" +
                            index
                          }
                        >
                          <GridItem
                            borderBottom={borderBottom}
                            justifySelf={"stretch"}
                            overflow={"hidden"}
                            textOverflow={"ellipsis"}
                            whiteSpace={"nowrap"}
                            title={name}
                          >
                            {name}
                          </GridItem>
                          <GridItem
                            borderBottom={borderBottom}
                            justifySelf={"stretch"}
                            textAlign={"end"}
                          >
                            {tCurrencyString(totalAmount, {
                              type: "SYMBOL",
                              code: currency,
                            })}
                          </GridItem>
                          <GridItem
                            borderBottom={borderBottom}
                            justifySelf={"stretch"}
                            textAlign={"end"}
                          >
                            {totalQuantity}
                          </GridItem>
                        </React.Fragment>
                      );
                    })}
                  <GridItem
                    borderTop={"0.5px solid var(--gray, #D4D4D1)"}
                    justifySelf={"stretch"}
                    fontWeight={"700"}
                  >
                    {tTitle("total")}
                  </GridItem>
                  <GridItem
                    borderTop={"0.5px solid var(--gray, #D4D4D1)"}
                    justifySelf={"stretch"}
                    textAlign={"end"}
                    fontWeight={"700"}
                  >
                    {tCurrencyString(
                      toBeUpdatedOrderSheetStatistic[0]?.totalAmount || 0,
                      { type: "SYMBOL", code: currency }
                    )}
                  </GridItem>
                  <GridItem
                    borderTop={"0.5px solid var(--gray, #D4D4D1)"}
                    justifySelf={"stretch"}
                    textAlign={"end"}
                    fontWeight={"700"}
                  >
                    {toBeUpdatedOrderSheetStatistic[0]?.totalQuantity || 0}
                  </GridItem>
                </Grid>
              )}
              {amountCheck.stage === "BELOW_RECOMMENDED" && moq != null && (
                <MoqNoticeBanner
                  moq={moq}
                  currency={currency}
                  showMinimum={false}
                />
              )}
            </Flex>
          )}
        </ModalBody>
        <ModalFooter display={"flex"} gap={"12px"}>
          <AppButton
            width={"144px"}
            isDisabled={isSubmitOrderSheetSetLoading}
            onClick={() => {
              onClose();
            }}
          >
            {tTitle("not_now")}
          </AppButton>
          <AppButton
            width={"144px"}
            variant={"primary"}
            isLoading={isSubmitOrderSheetSetLoading}
            onClick={handleTriggerClick}
          >
            {tTitle("submit")}
          </AppButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
